import { useRef, useState } from "react";
import { useAudioSettings } from "../context/AudioSettingsContext";
import { useSocket } from "../context/SocketContext";
import mioSound     from "../assets/mio.mp3";
import timeoutSound from "../assets/timeout.mp3";

// ── Reproduce localmente ─────────────────────────────────────────────────
function mkAudio(src, volume) {
  const a = new Audio(src);
  a.volume = Math.max(0, Math.min(1, volume));
  return a;
}

// ── Botones de test: local + remoto ──────────────────────────────────────
function TestButtons({ type, activeSrc, volume, muted, socket }) {
  const [playing, setPlaying] = useState(null); // null | "local" | "tv" | "overlay"

  const doLocal = () => {
    if (muted) return;
    const a = mkAudio(activeSrc, volume);
    setPlaying("local");
    a.play().catch(() => {});
    a.onended = () => setPlaying(null);
    setTimeout(() => setPlaying(null), 6000);
  };

  const doRemote = (target) => {
    if (!socket) return;
    socket.emit("sound:test", { type });
    setPlaying(target);
    setTimeout(() => setPlaying(null), 3000);
  };

  const btnStyle = (key) => ({
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "5px 11px",
    cursor: muted && key === "local" ? "not-allowed" : "pointer",
    fontWeight: 700,
    fontSize: 12,
    background: playing === key ? "#e0f2fe" : "#f8fafc",
    color: "#0369a1",
    touchAction: "manipulation",
    opacity: muted && key === "local" ? 0.45 : 1,
    transition: "background .15s",
  });

  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button style={btnStyle("local")}  onPointerDown={(e) => { e.preventDefault(); doLocal(); }}     title="Probar aquí en este dispositivo">
        {playing === "local"   ? "▶ …" : "▶ Local"}
      </button>
      <button style={btnStyle("tv")}     onPointerDown={(e) => { e.preventDefault(); doRemote("tv"); }}     title="Probar en TV">
        {playing === "tv"      ? "📺 …" : "📺 TV"}
      </button>
      <button style={btnStyle("overlay")} onPointerDown={(e) => { e.preventDefault(); doRemote("overlay"); }} title="Probar en overlay de sonido">
        {playing === "overlay" ? "📲 …" : "📲 Sonido"}
      </button>
    </div>
  );
}

// ── Control por sonido ───────────────────────────────────────────────────
function SoundControl({ label, icon, type, volume, muted, fileName, customUrl,
                        defaultSrc, onVolume, onMuted, onUpload, onRemove, socket }) {
  const inputRef   = useRef(null);
  const [uploading, setUploading] = useState(false);
  const activeSrc = customUrl || defaultSrc;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) { alert("Solo se admiten archivos de audio (.mp3, .ogg, .wav…)"); return; }
    setUploading(true);
    try { await onUpload(file); } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{label}</span>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <TestButtons type={type} activeSrc={activeSrc} volume={volume} muted={muted} socket={socket} />
          {/* Mute */}
          <button
            onPointerDown={(e) => { e.preventDefault(); onMuted(!muted); }}
            style={{ border: "none", borderRadius: 999, padding: "6px 14px", cursor: "pointer", fontWeight: 700, fontSize: 13, touchAction: "manipulation", background: muted ? "#fee2e2" : "#dcfce7", color: muted ? "#dc2626" : "#16a34a" }}>
            {muted ? "🔇 Silenciado" : "🔊 Activo"}
          </button>
        </div>
      </div>

      {/* Archivo */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f8f8f8", borderRadius: 8, flexWrap: "wrap" }}>
        <span style={{ flex: 1, fontSize: 12, color: fileName ? "var(--text)" : "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {fileName ? `🎵 ${fileName}` : "🔔 Sonido predeterminado"}
        </span>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {fileName && (
            <button onPointerDown={(e) => { e.preventDefault(); onRemove(); }}
              style={{ border: "1px solid #fecaca", borderRadius: 6, padding: "4px 10px", fontSize: 12, background: "#fff5f5", color: "#dc2626", cursor: "pointer", fontWeight: 600 }}>
              ✕ Quitar
            </button>
          )}
          <button onPointerDown={(e) => { e.preventDefault(); inputRef.current?.click(); }} disabled={uploading}
            style={{ border: "1px solid var(--brand-red)", borderRadius: 6, padding: "4px 12px", fontSize: 12, background: "var(--brand-red)", color: "#fff", cursor: "pointer", fontWeight: 700, touchAction: "manipulation" }}>
            {uploading ? "Subiendo…" : fileName ? "Cambiar" : "Subir MP3"}
          </button>
          <input ref={inputRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={handleFile} />
        </div>
      </div>

      {/* Volumen */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, opacity: muted ? 0.4 : 1, transition: "opacity .2s" }}>
        <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>🔈</span>
        <input type="range" min={0} max={1} step={0.01}
          value={muted ? 0 : volume} disabled={muted}
          onChange={(e) => onVolume(Number(e.target.value))}
          style={{ flex: 1, accentColor: "var(--brand-red)", cursor: muted ? "not-allowed" : "pointer" }} />
        <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>🔊</span>
        <span style={{ fontWeight: 700, fontSize: 14, minWidth: 42, textAlign: "right", color: "var(--brand-red)" }}>
          {muted ? "0%" : `${Math.round(volume * 100)}%`}
        </span>
      </div>
    </div>
  );
}

// ── Enlace espectadores ──────────────────────────────────────────────────
function ShareLink() {
  const url = `${window.location.origin}/sonido`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "16px 18px" }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>🎙️ Browser Source para TikTok Live Studio</div>
      <p style={{ fontSize: 12, opacity: 0.65, marginBottom: 12, marginTop: 0 }}>
        Añade esta URL como <strong>Browser Source</strong> en TikTok Live Studio u OBS. Es invisible (fondo transparente) y hace que los sonidos suenen por el directo automáticamente.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <code style={{ flex: 1, fontSize: 12, background: "#f4f4f5", padding: "8px 12px", borderRadius: 8, wordBreak: "break-all", border: "1px solid #e4e4e7" }}>
          {url}
        </code>
        <button onPointerDown={(e) => { e.preventDefault(); copy(); }}
          style={{ border: "1px solid var(--brand-red)", borderRadius: 8, padding: "7px 14px", background: copied ? "#dcfce7" : "#fff", color: copied ? "#16a34a" : "var(--brand-red)", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", touchAction: "manipulation" }}>
          {copied ? "✓ Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}



// ── Test de impresora ────────────────────────────────────────────────────
function PrinterTest() {
  const [status, setStatus] = useState(null); // null | "ok" | "error" | "loading"
  const [msg, setMsg]       = useState("");

  const test = async (type) => {
    setStatus("loading"); setMsg("");
    try {
      const res = await fetch("/api/printer/test", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setStatus("ok");
        setMsg(`Trabajo de ${type} enviado a la impresora ✓`);
      } else {
        setStatus("error");
        setMsg(data.error === "printer_offline"
          ? "Impresora offline — ¿está conectada la app PrintAgent?"
          : (data.error || "Error desconocido"));
      }
    } catch (e) {
      setStatus("error"); setMsg(String(e.message || e));
    }
    setTimeout(() => { setStatus(null); setMsg(""); }, 4000);
  };

  const btnBase = {
    padding: "9px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13,
    cursor: status === "loading" ? "not-allowed" : "pointer",
    border: "none", transition: "opacity .15s",
    opacity: status === "loading" ? 0.6 : 1,
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button style={{ ...btnBase, background: "#0ea5e9", color: "#fff" }}
          onClick={() => test("venta")} disabled={status === "loading"}>
          🖨️ Test venta
        </button>
        <button style={{ ...btnBase, background: "#8b5cf6", color: "#fff" }}
          onClick={() => test("subasta")} disabled={status === "loading"}>
          🖨️ Test subasta
        </button>
      </div>
      {msg && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 13,
          background: status === "ok" ? "#f0fdf4" : "#fff1f2",
          color:      status === "ok" ? "#15803d" : "#dc2626",
          border: `1px solid ${status === "ok" ? "#bbf7d0" : "#fecaca"}`,
        }}>
          {msg}
        </div>
      )}
      <p style={{ fontSize: 11, opacity: 0.5, marginTop: 8 }}>
        Manda una etiqueta ficticia a la impresora registrada como <strong>CAJA1</strong>.
        La app PrintAgent debe estar conectada.
      </p>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────
export default function Config() {
  const { settings, update, reset, uploadSound, removeSound, mioCustomUrl, timeoutCustomUrl } = useAudioSettings();
  const { socket } = useSocket();

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h1 className="cardTitle">Configuración</h1>

      {/* Sonidos */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
          🔉 Sonidos
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SoundControl
            label='Sonido "mio"'  icon="🗣️"  type="mio"
            volume={settings.mioVolume}    muted={settings.mioMuted}
            fileName={settings.mioFileName}   customUrl={mioCustomUrl}
            defaultSrc={mioSound}
            onVolume={(v) => update({ mioVolume: v })}
            onMuted={(m)  => update({ mioMuted: m })}
            onUpload={(f) => uploadSound("mio", f)}
            onRemove={()  => removeSound("mio")}
            socket={socket}
          />
          <SoundControl
            label="Timeout (venta / subasta)"  icon="⏱️"  type="timeout"
            volume={settings.timeoutVolume}  muted={settings.timeoutMuted}
            fileName={settings.timeoutFileName}  customUrl={timeoutCustomUrl}
            defaultSrc={timeoutSound}
            onVolume={(v) => update({ timeoutVolume: v })}
            onMuted={(m)  => update({ timeoutMuted: m })}
            onUpload={(f) => uploadSound("timeout", f)}
            onRemove={()  => removeSound("timeout")}
            socket={socket}
          />
        </div>
      </div>

      {/* Enlace espectadores */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
          🎙️ Live Studio
        </div>
        <ShareLink />
      </div>

      {/* Test impresora */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
          🖨️ Impresora
        </div>
        <PrinterTest />
      </div>

      {/* Reset */}
      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-outline" style={{ fontSize: 13 }}
          onPointerDown={(e) => { e.preventDefault(); reset(); }}>
          Restablecer valores por defecto
        </button>
      </div>

      <p className="muted" style={{ fontSize: 11, marginTop: 14 }}>
        Configuración guardada en este dispositivo. Los sonidos personalizados se almacenan en IndexedDB.
      </p>
    </div>
  );
}
